import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar, type VariantType } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { LoadingBox } from "@/components/ui";
import { AppConfigDisplay } from "../components/app-config-display";
import { WebSocketProvider } from "../contexts/websocket-context";
import { useApiQuery } from "../hooks";
import type { ApiConfig, AppConfig } from "../types";
import type {
  TestAgentV2Event,
  TestAgentV2IncomingEvent,
  TestAgentV2NotifyEvent,
} from "../types/test-agent-2";
import { GenerateFastChat } from "./generate-fast-chat";
import { GenerateFastViewer } from "./generate-fast-viewer";

interface GeneratePageInnerProps {
  selectedAppConfigId: string | null;
  nonLocalAppConfigs: AppConfig[];
}

function GeneratePageInner({ selectedAppConfigId }: GeneratePageInnerProps) {
  const { enqueueSnackbar } = useSnackbar();

  const queryParams: Record<string, string> = useMemo(() => {
    if (selectedAppConfigId) {
      return { app_config_id: selectedAppConfigId } as Record<string, string>;
    }
    return {};
  }, [selectedAppConfigId]);

  return (
    <Box data-testid="generate-fast">
      <WebSocketProvider<TestAgentV2IncomingEvent, TestAgentV2Event>
        key={selectedAppConfigId || "default"}
        path="test-agent-2"
        useAuth
        queryParams={queryParams}
        onMessageCallback={(message) => {
          if (message.type !== "notify") return;

          const noti = message.data as TestAgentV2NotifyEvent;
          const level = noti.level as VariantType;

          enqueueSnackbar(noti.message, {
            variant: level,
            anchorOrigin: {
              horizontal: "left",
              vertical: "bottom",
            },
          });
        }}
      >
        <PanelGroup direction="horizontal">
          <Panel defaultSize={40} minSize={30} maxSize={70}>
            <GenerateFastChat />
          </Panel>
          <PanelResizeHandle>
            <Divider orientation="vertical">
              <MultipleStopIcon color="disabled" fontSize="small" />
            </Divider>
          </PanelResizeHandle>
          <Panel defaultSize={60} minSize={30} maxSize={70}>
            <GenerateFastViewer />
          </Panel>
        </PanelGroup>
      </WebSocketProvider>
    </Box>
  );
}

const QA_JOKES = [
  "Loading tests faster than bugs can hide! 🐛",
  "Please wait while we generate some quality chaos... 🎯",
  "Loading... because good tests take time! ⏱️",
  "Preparing to break things professionally... 🔨",
  "Loading edge cases and coffee... ☕",
  "Generating tests that will find ALL the bugs! 🔍",
  "Please wait while we prep the chaos engine... 🚀",
  "Loading... testing your patience first! 😄",
  "Warming up the bug detector... 🕵️",
  "Loading tests designed to make devs cry... 😈",
];

export default function GeneratePage() {
  const [isLoading, setIsLoading] = useState(true);
  const [randomJoke, setRandomJoke] = useState("");
  const [selectedAppConfigId, setSelectedAppConfigId] = useState<string | null>(null);

  // Check for appId query param
  const params = new URLSearchParams(window.location.search);
  const appIdFromQuery = params.get("appId");

  const { data: config, isLoading: isLoadingAppConfigs } = useApiQuery<ApiConfig>("/config");

  const wsForAppConfig = useCallback(
    (appConfig: AppConfig) => {
      const maybeApiKey = config?.api_keys?.find((k) => k.app_config_id === appConfig.id);

      if (!maybeApiKey) return null;

      if (
        appConfig.cfg_type === "local" &&
        maybeApiKey.last_used_at &&
        Date.now() - new Date(maybeApiKey.last_used_at).getTime() <
          // 5 minutes
          5 * 60 * 1000
      ) {
        return maybeApiKey.ws_url;
      }

      return null;
    },
    [config],
  );

  const nonLocalAppConfigs = useMemo(() => {
    return (
      config?.configs?.filter((ac) => {
        if (ac.cfg_type !== "local") return true;
        return !!wsForAppConfig(ac);
      }) || []
    );
  }, [config, wsForAppConfig]);

  // Auto-select from query param or if only one config exists
  useEffect(() => {
    if (selectedAppConfigId) return;

    // First priority: appId from query param
    if (appIdFromQuery && nonLocalAppConfigs.length > 0) {
      const configFromQuery = nonLocalAppConfigs.find((c) => c.id === appIdFromQuery);
      if (configFromQuery) {
        setSelectedAppConfigId(configFromQuery.id);
        return;
      }
    }

    // Second priority: auto-select if only one config
    if (nonLocalAppConfigs.length === 1) {
      setSelectedAppConfigId(nonLocalAppConfigs[0].id);
    }
  }, [nonLocalAppConfigs, selectedAppConfigId, appIdFromQuery]);

  // Start loading animation when app config is selected
  useEffect(() => {
    if (!selectedAppConfigId) {
      return;
    }

    setIsLoading(true);
    const joke = QA_JOKES[Math.floor(Math.random() * QA_JOKES.length)];
    setRandomJoke(joke);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [selectedAppConfigId]);

  // Show app config selector first, before loading
  if (isLoadingAppConfigs) {
    return <LoadingBox sx={{ minHeight: "calc(100vh - 120px)" }} />;
  }

  if (nonLocalAppConfigs.length > 1 && !selectedAppConfigId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 120px)",
          padding: 4,
        }}
      >
        <Card sx={{ maxWidth: 600, width: "100%" }}>
          <CardContent>
            <Stack direction="column" spacing={2}>
              <Typography variant="h6">Select App Configuration</Typography>
              <Typography variant="body2" color="text.secondary">
                You have multiple app configurations. Please select which one you want to use for
                this test session.
              </Typography>
              <Autocomplete
                options={nonLocalAppConfigs}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="App Configuration"
                    placeholder="Select an app config..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <AppConfigDisplay appConfig={option} />
                  </Box>
                )}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setSelectedAppConfigId(newValue.id);
                    setIsLoading(true);
                  }
                }}
                fullWidth
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "calc(100vh - 120px)",
          padding: 4,
        }}
      >
        <Stack direction="column" spacing={4} alignItems="center" maxWidth={600} width="100%">
          <Stack spacing={2} width="100%" maxWidth={800}>
            <Skeleton variant="rectangular" width="60%" height={24} />
            <Skeleton variant="rectangular" width="100%" height={32} />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="rectangular" width="30%" height={20} />
              <Skeleton variant="rectangular" width="25%" height={20} />
              <Skeleton variant="rectangular" width="35%" height={20} />
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={18} color="inherit" sx={{ color: "text.secondary" }} />
            <Typography variant="h6" color="text.secondary" align="center">
              {randomJoke}
            </Typography>
          </Stack>

          <Stack spacing={2} width="100%" maxWidth={800}>
            <Skeleton variant="rectangular" width="100%" height={60} />
            <Stack direction="row" spacing={2}>
              <Skeleton variant="rectangular" width="48%" height={80} />
              <Skeleton variant="rectangular" width="48%" height={80} />
            </Stack>
            <Skeleton variant="rectangular" width="100%" height={40} />
            <Skeleton variant="rectangular" width="70%" height={30} />
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Fade in={!isLoading} timeout={600}>
      <Box className="generate-page">
        <GeneratePageInner
          selectedAppConfigId={selectedAppConfigId}
          nonLocalAppConfigs={nonLocalAppConfigs}
        />
      </Box>
    </Fade>
  );
}
